import { NavBar } from "@components/navbar/nav-bar";
import "typeface-roboto";
import { useLoggedInUser } from "@hooks/use-logged-in-user";
import { LoadingScreen } from "@flat-ui";
import { useAppNavigation } from "@router/use-app-navigation";
import { useMemo } from "react";
import { Outlet } from "react-router-dom";
import { useTrackPageLoad } from "@utils/hooks/use-track-page-load";
import { KeyBindingsLoader } from "@loaders/key-bindings-loader";
import { QueryParamsLoader } from "@loaders/query-params-loader";
import { runtimeConfig } from "@src/runtime-config";
import { LicenseInfo } from "@mui/x-license";
import { AccessDeniedPage } from "@pages/access-denied.page";

/** The root component of the admin-panel */
export function App(): React.JSX.Element {
  if (runtimeConfig.features.muiProKey) {
    LicenseInfo.setLicenseKey(runtimeConfig.features.muiProKey);
  }

  const { currentUser, fetchingCurrentUser, fetchingCurrentUserRoles } =
    useLoggedInUser();
  const { navigateToLoginPage } = useAppNavigation();

  // Initializes the tracking of the loggedInUser in the entry page API
  useTrackPageLoad();

  const content = useMemo(() => {
    // If any of the requests for fetching the user data and the user roles is still loading, show the loading screen
    if (
      fetchingCurrentUser === "pending" ||
      fetchingCurrentUser === "uninitialized" ||
      fetchingCurrentUserRoles === "pending" ||
      fetchingCurrentUserRoles === "uninitialized"
    ) {
      return <LoadingScreen />;
    }

    // If fetching the user roles failed but fetching the user data did not fail,
    // It means that the user is logged in but it doesn't have permissions to access the admin panel,
    // so we show an error dialog with the option to log out.
    if (currentUser && fetchingCurrentUserRoles === "rejected") {
      return <AccessDeniedPage />;
    }

    // If fetching the current user and fetching the current user roles succeeded,
    // it means that the user is logged in and has permissions to access the admin panel, so we show the app content.
    if (currentUser) {
      return (
        <>
          <header>
            <NavBar />
          </header>
          <main>
            <Outlet />
          </main>
        </>
      );
    }

    // Otherwise, it means that the user is not logged in, so we navigate to the login page.
    return <>{navigateToLoginPage()}</>;
  }, [
    fetchingCurrentUser,
    fetchingCurrentUserRoles,
    currentUser,
    navigateToLoginPage,
  ]);

  return (
    <QueryParamsLoader>
      <KeyBindingsLoader>{content}</KeyBindingsLoader>
    </QueryParamsLoader>
  );
}
